import { useState, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { AlertTriangle, CheckCircle2, Clock, Flag, X, Users, Play, Pause, CircleDot, ListTodo, Bell, CalendarDays } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { useTasks } from '@/contexts/TaskContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const COLORS = ['#10B981', '#F59E0B', '#EF4444', '#64748B'];
const STATUS_COLORS = {
  'done': '#10B981',
  'pending': '#EF4444',
  'yet to start': '#3B82F6',
  'in progress': '#F59E0B',
  'on hold': '#64748B',
};
const getStatusColor = (statusName) => STATUS_COLORS[String(statusName).toLowerCase()] || '#94A3B8';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = () => {
  const [snapshotEmployeeId, setSnapshotEmployeeId] = useState('');
  const [zeroTaskMembers, setZeroTaskMembers] = useState([]);
  const [extensionRequests, setExtensionRequests] = useState([]);
  const [alertsPanelOpen, setAlertsPanelOpen] = useState(false);

  const fetchAlerts = async () => {
    try {
      const [zeroTaskRes, extensionRes] = await Promise.all([
        axios.get(`${API}/notifications/zero-task-employees`),
        axios.get(`${API}/extension-requests/pending`)
      ]);
      setZeroTaskMembers(zeroTaskRes.data.members || []);
      setExtensionRequests(extensionRes.data.requests || []);
    } catch (error) {
      console.error('Error fetching alerts:', error);
    }
  };

  useEffect(() => {
    fetchAlerts();
  }, []);
  // Read from the global TaskContext (single source of truth).
  // activeTasks already excludes archivedGlobally tasks and tasks from archived accounts.
  const { activeTasks: tasks, tasks: allTasksRaw, accounts, teamMembers, stats, loading } = useTasks();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [dialogTasks, setDialogTasks] = useState([]);
  const [dialogType, setDialogType] = useState('tasks'); // 'tasks' or 'members'

  // Build set of active account IDs for filtering
  const activeAccountIds = new Set(accounts.map(a => a.id));

  // Filter tasks to only include those from active accounts (for stats/charts)
  const activeAccountTasks = tasks.filter(t => 
    !t.accountId || activeAccountIds.has(t.accountId)
  );

  // Get tasks filtered by status (uses activeAccountTasks for stats)
  const getTasksByStatus = (status) => {
    // "done" intentionally includes archived tasks (so completed work still counts).
    // Every other status excludes archived tasks (they're hidden from view elsewhere).
    const notArchived = t => !t.archived && !t.archivedGlobally;
    if (status === 'all') return activeAccountTasks.filter(notArchived);
    if (status === 'active') return activeAccountTasks.filter(t => notArchived(t) && !['Done', 'done', 'completed'].includes(t.status));
    if (status === 'in_progress') return activeAccountTasks.filter(t => notArchived(t) && ['In Progress', 'in_progress', 'ongoing'].includes(t.status));
    if (status === 'yet_to_start') return activeAccountTasks.filter(t => notArchived(t) && ['Yet to Start', 'pending', 'Pending'].includes(t.status));
    if (status === 'on_hold') return activeAccountTasks.filter(t => notArchived(t) && ['On Hold', 'on_hold', 'blocked'].includes(t.status));
    if (status === 'done') return activeAccountTasks.filter(t => ['Done', 'done', 'completed'].includes(t.status));
    return [];
  };

  // Get count for each status
  const getStatusCount = (status) => getTasksByStatus(status).length;

  // Handlers for clicking on stat cards
  const handleStatClick = (statType) => {
    let filteredTasks = [];
    let title = '';
    let type = 'tasks';

    switch (statType) {
      case 'total':
        filteredTasks = getTasksByStatus('active'); // Exclude done tasks
        title = 'Active Tasks';
        break;
      case 'yetToStart':
        filteredTasks = getTasksByStatus('yet_to_start');
        title = 'Yet to Start';
        break;
      case 'inProgress':
        filteredTasks = getTasksByStatus('in_progress');
        title = 'In Progress';
        break;
      case 'onHold':
        filteredTasks = getTasksByStatus('on_hold');
        title = 'On Hold';
        break;
      case 'done':
        filteredTasks = getTasksByStatus('done');
        title = 'Done';
        break;
      case 'overdue':
        filteredTasks = activeAccountTasks.filter(t => t.isLagging);
        title = 'Overdue Tasks';
        break;
      case 'highPriority':
        filteredTasks = activeAccountTasks.filter(t => t.priority === 'high');
        title = 'High Priority Tasks';
        break;
      case 'laggingMembers':
        type = 'members';
        title = 'Lagging Members';
        break;
      default:
        return;
    }

    // Sort tasks by priority and due date
    filteredTasks = filteredTasks.sort((a, b) => {
      const priorityOrder = { high: 0, med: 1, low: 2 };
      const priorityDiff = (priorityOrder[a.priority] || 2) - (priorityOrder[b.priority] || 2);
      if (priorityDiff !== 0) return priorityDiff;
      if (a.dueDate && b.dueDate) return new Date(a.dueDate) - new Date(b.dueDate);
      return 0;
    });

    setDialogTasks(filteredTasks);
    setDialogTitle(title);
    setDialogType(type);
    setDialogOpen(true);
  };

  // Get lagging members
  const getLaggingMembers = () => {
    return teamMembers.filter(m => m.laggingTaskCount >= 3);
  };

  // Get member name by ID
  const getMemberName = (id) => {
    const member = teamMembers.find(m => m.id === id);
    return member ? member.name : 'Unknown';
  };

  const getStatusDistribution = () => {
    const statusCounts = activeAccountTasks.reduce((acc, task) => {
      const status = task.status || 'pending';
      acc[status] = (acc[status] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(statusCounts).map(([name, value]) => ({ name, value }));
  };

  const getAccountDistribution = () => {
    const accountCounts = activeAccountTasks.reduce((acc, task) => {
      const account = task.accountName || 'Unassigned';
      acc[account] = (acc[account] || 0) + 1;
      return acc;
    }, {});

    return Object.entries(accountCounts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  const statCards = [
    {
      title: 'Active Tasks',
      value: getStatusCount('active'),
      icon: ListTodo,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      filterType: 'total'
    },
    {
      title: 'Yet to Start',
      value: getStatusCount('yet_to_start'),
      icon: CircleDot,
      color: 'text-amber-600',
      bgColor: 'bg-amber-100',
      filterType: 'yetToStart'
    },
    {
      title: 'In Progress',
      value: getStatusCount('in_progress'),
      icon: Play,
      color: 'text-blue-600',
      bgColor: 'bg-blue-100',
      filterType: 'inProgress'
    },
    {
      title: 'On Hold',
      value: getStatusCount('on_hold'),
      icon: Pause,
      color: 'text-orange-600',
      bgColor: 'bg-orange-100',
      filterType: 'onHold'
    },
    {
      title: 'Done',
      value: getStatusCount('done'),
      icon: CheckCircle2,
      color: 'text-green-600',
      bgColor: 'bg-green-100',
      filterType: 'done'
    },
    {
      title: 'Overdue',
      value: stats?.overdueTasks || 0,
      icon: AlertTriangle,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      filterType: 'overdue'
    },
    {
      title: 'High Priority',
      value: stats?.highPriorityTasks || 0,
      icon: Flag,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      filterType: 'highPriority'
    },
    {
      title: 'Lagging Members',
      value: getLaggingMembers().length,
      icon: Users,
      color: 'text-red-600',
      bgColor: 'bg-red-100',
      filterType: 'laggingMembers'
    }
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
      className="p-8 space-y-6"
      data-testid="dashboard-page"
    >
      {/* Alerts Button - fixed top-right corner */}
      <div className="fixed top-4 right-4 z-40">
        <button
          onClick={() => setAlertsPanelOpen(true)}
          className="flex items-center gap-2 px-3 py-2 bg-white border border-border rounded-lg shadow-sm hover:bg-muted transition-colors"
          data-testid="alerts-button"
        >
          <Bell className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium">Alerts</span>
          {(zeroTaskMembers.length + extensionRequests.length) > 0 && (
            <span className="bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
              {zeroTaskMembers.length + extensionRequests.length}
            </span>
          )}
        </button>
      </div>
      <Dialog open={alertsPanelOpen} onOpenChange={setAlertsPanelOpen}>
        <DialogContent className="max-w-lg bg-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Alerts</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <p className="text-sm font-semibold mb-2">Zero-Task Employees ({zeroTaskMembers.length})</p>
              {zeroTaskMembers.length === 0 ? (
                <p className="text-xs text-muted-foreground">Everyone has at least one task.</p>
              ) : (
                <ul className="space-y-1">
                  {zeroTaskMembers.map((m) => (
                    <li key={m.id} className="text-sm text-red-600">{m.name}</li>
                  ))}
                </ul>
              )}
            </div>
            <div>
              <p className="text-sm font-semibold mb-2">Pending Extension Requests ({extensionRequests.length})</p>
              {extensionRequests.length === 0 ? (
                <p className="text-xs text-muted-foreground">No pending requests.</p>
              ) : (
                <ul className="space-y-2">
                  {extensionRequests.map((req) => (
                    <li key={req.taskId} className="text-sm p-2 bg-orange-50 border border-orange-200 rounded">
                      <span className="font-medium">{req.taskTitle}</span> - {req.requestedBy} requested {req.requestedDate ? format(new Date(req.requestedDate), 'MMM dd, yyyy') : '-'}
                    </li>
                  ))}
                </ul>
              )}
              <p className="text-xs text-muted-foreground mt-2">Go to Task Management to approve or reject.</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <div>
        <h1 className="text-2xl sm:text-4xl font-bold tracking-tight">Operations Dashboard</h1>
        <p className="text-muted-foreground mt-1">Real-time visibility into your team's performance</p>
      </div>

      {/* Employee Snapshot */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-4">
          <Users className="h-5 w-5 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Employee Snapshot</h2>
        </div>
        <div className="flex items-center gap-2">
          <Select value={snapshotEmployeeId} onValueChange={setSnapshotEmployeeId}>
            <SelectTrigger className="w-64 bg-white">
              <SelectValue placeholder="Select an employee" />
            </SelectTrigger>
            <SelectContent className="bg-white">
              {teamMembers.map((m) => (
                <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {snapshotEmployeeId && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSnapshotEmployeeId('')}
              data-testid="clear-snapshot-employee"
            >
              Clear
            </Button>
          )}
        </div>
        {snapshotEmployeeId && (() => {
          const getRoles = (t) => {
            const roles = [];
            if (t.responsible?.includes(snapshotEmployeeId)) roles.push('R');
            if (t.accountable?.includes(snapshotEmployeeId)) roles.push('A');
            if (t.consulted?.includes(snapshotEmployeeId)) roles.push('C');
            if (t.informed?.includes(snapshotEmployeeId)) roles.push('I');
            return roles;
          };
          const roleColors = { R: 'bg-blue-500', A: 'bg-purple-500', C: 'bg-green-500', I: 'bg-gray-500' };
          const snapshotTasks = tasks
            .filter(t =>
              getRoles(t).length > 0 &&
              t.status?.toLowerCase() !== 'done' &&
              !t.archived && !t.archivedGlobally
            )
            .sort((a, b) => {
              if (!a.dueDate) return 1;
              if (!b.dueDate) return -1;
              return new Date(a.dueDate) - new Date(b.dueDate);
            });
          return (
            <div className="mt-4 space-y-2">
              {snapshotTasks.length === 0 ? (
                <p className="text-sm text-muted-foreground">No active tasks for this employee.</p>
              ) : (
                snapshotTasks.map((t) => (
                  <div
                    key={t.id}
                    className={`p-3 rounded-lg border ${t.isLagging ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'}`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getRoles(t).map(r => (
                          <span key={r} className={`px-1.5 py-0.5 ${roleColors[r]} text-white rounded text-xs font-bold`}>{r}</span>
                        ))}
                        <p className="font-medium">{t.title}</p>
                      </div>
                      {t.isLagging && <span className="text-xs text-red-600 font-semibold">Overdue</span>}
                    </div>
                    <div className="flex flex-wrap gap-x-4 text-xs text-muted-foreground mt-1">
                      <span>Client: {t.accountName || 'N/A'}</span>
                      <span>Due: {t.dueDate ? format(new Date(t.dueDate), 'MMM dd, yyyy') : 'Not set'}</span>
                      {t.assignedDate && <span>Assigned: {format(new Date(t.assignedDate), 'MMM dd, yyyy')}</span>}
                    </div>
                  </div>
                ))
              )}
            </div>
          );
        })()}
      </Card>
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {statCards.map((stat, index) => (
          <motion.div
            key={stat.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.05 }}
          >
            <Card 
              className="border-border/60 hover:shadow-lg transition-all cursor-pointer bg-white hover:scale-[1.02]" 
              data-testid={`stat-card-${stat.title.toLowerCase().replace(' ', '-')}`}
              onClick={() => handleStatClick(stat.filterType)}
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground font-medium">{stat.title}</p>
                    <p className="text-3xl font-bold mt-2">{stat.value}</p>
                    <p className="text-xs text-muted-foreground mt-1">Click to view</p>
                  </div>
                  <div className={`${stat.bgColor} ${stat.color} p-3 rounded-lg`}>
                    <stat.icon className="h-5 w-5" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Status Distribution */}
        <Card className="border-border/60 bg-white" data-testid="status-distribution-chart">
          <CardHeader>
            <CardTitle className="text-xl">Task Status Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={getStatusDistribution()}
                  cx="50%"
                  cy="50%"
                  outerRadius={90}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {getStatusDistribution().map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getStatusColor(entry.name)} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend verticalAlign="bottom" height={60} wrapperStyle={{ fontSize: '12px' }} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Accounts */}
        <Card className="border-border/60 bg-white" data-testid="top-accounts-chart">
          <CardHeader>
            <CardTitle className="text-xl">Top Accounts by Task Count</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={getAccountDistribution()}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis />
                <Tooltip />
                <Bar dataKey="count" fill="#4F46E5" radius={[8, 8, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Team Performance */}
      <Card className="border-border/60 bg-white" data-testid="team-performance-section">
        <CardHeader>
          <CardTitle className="text-xl">Team Performance</CardTitle>
          <p className="text-sm text-muted-foreground">
            {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}
          </p>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/60">
                  <th className="text-left py-2 pr-4 font-medium text-muted-foreground">Member</th>
                  <th className="text-center py-2 px-4 font-medium text-muted-foreground">Assigned</th>
                  <th className="text-center py-2 px-4 font-medium text-muted-foreground">Completed</th>
                  <th className="text-center py-2 px-4 font-medium text-muted-foreground">Lagging</th>
                  <th className="text-center py-2 px-4 font-medium text-muted-foreground">Status</th>
                </tr>
              </thead>
              <tbody>
                {teamMembers
                  .map((member) => {
                    const now = new Date();
                    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1);

                    const memberTasks = tasks.filter(t =>
                      !t.archived && !t.archivedGlobally &&
                      !(t.archivedByUsers || []).includes(member.id) &&
                      [...(t.responsible || []),
                       ...(t.accountable || []),
                       ...(t.consulted || [])]
                      .includes(member.id)
                    );

                    const assignedThisMonth = memberTasks.filter(t =>
                      t.status?.toLowerCase() !== 'done' && !t.archived && !t.archivedGlobally
                    ).length;
                    // Done count should include archived tasks too - completing work and archiving
                    // it afterward shouldn't make it stop counting as completed.
                    const memberTasksIncludingArchived = allTasksRaw.filter(t =>
                      [...(t.responsible || []),
                       ...(t.accountable || []),
                       ...(t.consulted || [])]
                      .includes(member.id)
                    );
                    const completedThisMonth = memberTasksIncludingArchived.filter(t =>
                      t.status?.toLowerCase() === 'done' &&
                      t.completedDate && new Date(t.completedDate) >= monthStart
                    ).length;

                    const laggingCount = memberTasks.filter(t =>
                      t.isLagging && t.status?.toLowerCase() !== 'done'
                    ).length;

                    return {
                      ...member,
                      assignedThisMonth,
                      completedThisMonth,
                      laggingCount,
                    };
                  })
                  .sort((a, b) => b.laggingCount - a.laggingCount)
                  .map((member) => {
                    const statusColor =
                      member.laggingCount === 0 ? 'bg-green-500' :
                      member.laggingCount <= 2  ? 'bg-amber-400' :
                                                  'bg-red-500';
                    const statusLabel =
                      member.laggingCount === 0 ? 'On Track' :
                      member.laggingCount <= 2  ? 'At Risk' :
                                                  'Lagging';

                    return (
                      <tr key={member.id} className="border-b border-border/40 hover:bg-muted/30 transition-colors">
                        <td className="py-3 pr-4">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center text-xs font-semibold text-primary">
                              {member.name?.charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium">{member.name}</span>
                          </div>
                        </td>
                        <td className="text-center py-3 px-4 tabular-nums">{member.assignedThisMonth}</td>
                        <td className="text-center py-3 px-4 tabular-nums">{member.completedThisMonth}</td>
                        <td className="text-center py-3 px-4 tabular-nums font-medium">
                          {member.laggingCount > 0 ? (
                            <span className="text-red-500">{member.laggingCount}</span>
                          ) : (
                            <span className="text-green-500">0</span>
                          )}
                        </td>
                        <td className="text-center py-3 px-4">
                          <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium text-white ${statusColor}`}>
                            {statusLabel}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Task Details Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">{dialogTitle}</DialogTitle>
          </DialogHeader>
          
          {dialogType === 'tasks' ? (
            <div className="mt-4 space-y-3">
              {dialogTasks.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No tasks found</p>
              ) : (
                dialogTasks.map((task) => (
                  <div
                    key={task.id}
                    className={`p-4 rounded-lg border ${
                      task.priority === 'high' ? 'bg-red-50 border-red-200' :
                      task.priority === 'med' ? 'bg-yellow-50 border-yellow-200' :
                      'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold">{task.title}</h3>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            task.status === 'Done' ? 'bg-green-100 text-green-700' :
                            task.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                            task.status === 'On Hold' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {task.status}
                          </span>
                          <span className={`px-2 py-0.5 rounded text-xs font-bold ${
                            task.priority === 'high' ? 'bg-red-500 text-white' :
                            task.priority === 'med' ? 'bg-yellow-500 text-white' :
                            'bg-green-500 text-white'
                          }`}>
                            {task.priority?.toUpperCase()}
                          </span>
                        </div>
                        {task.deliverable && (
                          <p className="text-sm text-muted-foreground mt-1">{task.deliverable}</p>
                        )}
                        {task.accountName && (
                          <p className="text-xs text-muted-foreground mt-1">Account: {task.accountName}</p>
                        )}
                        {task.responsible && task.responsible.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            Responsible: {task.responsible.map(id => getMemberName(id)).join(', ')}
                          </p>
                        )}
                      </div>
                      <div className="text-right text-sm">
                        {task.dueDate && (
                          <p className={`font-mono ${task.isLagging ? 'text-red-600 font-semibold' : 'text-muted-foreground'}`}>
                            Due: {format(new Date(task.dueDate), 'MMM dd')}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            <div className="mt-4 space-y-3">
              {getLaggingMembers().length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No lagging members - great job!</p>
              ) : (
                getLaggingMembers().map((member) => (
                  <div
                    key={member.id}
                    className="p-4 rounded-lg border bg-red-50 border-red-200"
                  >
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-red-200 flex items-center justify-center text-lg font-semibold text-red-700">
                        {member.name.charAt(0)}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold">{member.name}</h3>
                        <p className="text-sm text-red-600">
                          {member.laggingTaskCount} overdue task{member.laggingTaskCount > 1 ? 's' : ''} out of {member.taskCount} total
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                          <AlertTriangle className="h-4 w-4" />
                          Behind Schedule
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </motion.div>
  );
};

export default Dashboard;
